import type React from "react";

import { cn } from "../../utils/cn";

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
    /**
     * Surface tone within the Paper family. Determines the bg + the
     * foreground/border token family the consumer composes children
     * against (see `card.intent.md`).
     *   raised  — `bg-surface-raised`. Default contained-card treatment.
     *   sunken  — `bg-surface-sunken`. Recessed treatment for nested
     *             cards inside another card.
     *   overlay — `bg-surface-overlay`. Modals and floating panels.
     *   ground  — `bg-surface-ground`. No contrasting background; the
     *             border alone defines the card. Use with `border="line"`
     *             for the "outline card" recipe.
     */
    surface?: "raised" | "sunken" | "overlay" | "ground";

    /**
     * Full-perimeter border. Default `"line"` — the standard 1px
     * neutral outline. Set a tone (`"accent" | "error" | "success" |
     * "warning"`) for whole-card attention-needed treatments
     * (rejected slot cards, decline banners). Set `false` for
     * borderless cards (charts and tables on `surface="raised"` —
     * the surface contrast carries the visual without an outline).
     */
    border?: "line" | "accent" | "error" | "success" | "warning" | false;

    /**
     * Replaces the top of the border with a colored stripe. Used for
     * directional emphasis — featured cards, tab-style indicators,
     * attention markers. Orthogonal to `border`: the stripe replaces
     * only the top, leaving left/right/bottom under the base border
     * setting.
     */
    topStripe?: {
        /** Color tone of the stripe. */
        tone: "accent" | "error" | "success" | "warning";
        /** Stripe thickness in `px`. Default `2` — visible by design. */
        thickness?: 1 | 2;
    };

    /**
     * Default `true` → `rounded-xs` corner radius. Set `false` for
     * sharp corners (some legacy detail-panel cards use this).
     */
    rounded?: boolean;

    /**
     * Render as a different semantic element. Defaults to `"div"`. Use
     * `"section"` for page-level data containers, `"article"` for
     * self-contained content units, `"aside"` for tangential blocks.
     */
    asElement?: "div" | "section" | "article" | "aside";

    children: React.ReactNode;
    className?: string;
}

const SURFACE_CLASS: Record<NonNullable<CardProps["surface"]>, string> = {
    raised: "bg-surface-raised",
    sunken: "bg-surface-sunken",
    overlay: "bg-surface-overlay",
    ground: "bg-surface-ground"
};

const BORDER_CLASS: Record<
    Exclude<CardProps["border"], false | undefined>,
    string
> = {
    line: "border border-line-base",
    accent: "border border-accent",
    error: "border border-error-base",
    success: "border border-success-base",
    warning: "border border-warning-base"
};

const STRIPE_TONE_CLASS: Record<
    NonNullable<CardProps["topStripe"]>["tone"],
    string
> = {
    accent: "border-t-accent",
    error: "border-t-error-base",
    success: "border-t-success-base",
    warning: "border-t-warning-base"
};

export function Card({
    surface = "raised",
    border = "line",
    topStripe,
    rounded = true,
    asElement = "div",
    children,
    className,
    ...rest
}: CardProps): React.ReactElement {
    // The asElement prop is constrained to a small union; cast through
    // a generic React element creator so TypeScript doesn't fight us.
    const Tag = asElement as React.ElementType;

    return (
        <Tag
            className={cn(
                SURFACE_CLASS[surface],
                border !== false && BORDER_CLASS[border],
                topStripe && [
                    // Replace the top of the base border with the
                    // stripe. tailwind-merge resolves the
                    // border-t-{0,2} / border-t-{color} collision so
                    // the stripe wins.
                    topStripe.thickness === 1 ? "border-t" : "border-t-2",
                    STRIPE_TONE_CLASS[topStripe.tone]
                ],
                rounded && "rounded-xs",
                className
            )}
            {...rest}
        >
            {children}
        </Tag>
    );
}
